import React, { useState, useEffect } from "react";
import axios from "axios";
import { baseurl } from '../utility/helper';

// ── Axios helper: injects auth token from sessionStorage ──────────────────────
const authHeader = () => {
  const token = sessionStorage.getItem("token");
  return {
    headers: {
      Authorization: `${token}`,
      "Content-Type": "application/json",
    },
  };
};

// ─── Icons ───────────────────────────────────────────────────────────────────
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" />
  </svg>
);
const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);
const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const EyeOffIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

// ─── Delete Confirmation Modal ────────────────────────────────────────────────
const DeleteModal = ({ item, onConfirm, onCancel }) => (
  <div style={styles.modalOverlay}>
    <div style={styles.modalBox}>
      <div style={styles.modalIcon}>⚠</div>
      <h3 style={styles.modalTitle}>Delete Configuration?</h3>
      <p style={styles.modalText}>
        You're about to delete the PayU config for{" "}
        <strong>{item?.chapter_name}</strong>. This action cannot be undone.
      </p>
      <div style={styles.modalActions}>
        <button onClick={onCancel} style={styles.cancelBtn}>Cancel</button>
        <button onClick={onConfirm} style={styles.deleteConfirmBtn}>Yes, Delete</button>
      </div>
    </div>
  </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────
const PaymentSetting = () => {
  const [view, setView]                     = useState("list");
  const [configs, setConfigs]               = useState([]);
  const [chapters, setChapters]             = useState([]);
  const [loadingChapters, setLoadingChapters] = useState(true);
  const [loadingConfigs, setLoadingConfigs]   = useState(true);
  const [editItem, setEditItem]             = useState(null);
  const [deleteTarget, setDeleteTarget]     = useState(null);
  const [showSalt, setShowSalt]             = useState(false);
  const [showKey, setShowKey]               = useState(false);
  const [toast, setToast]                   = useState(null);
  const [submitting, setSubmitting]         = useState(false);

  const emptyForm = { chapter_id: "",  payu_key: "", payu_salt: "", is_active: true };
  const [form, setForm]     = useState(emptyForm);
  const [errors, setErrors] = useState({});

  // ── Toast ──
  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Fetch chapters ──────────────────────────────────────────────────────────
useEffect(() => {
  const fetchChapters = async () => {
    try {
      setLoadingChapters(true);
      const response = await axios.get(`${baseurl}/admin/chapters`, authHeader());

      // Filter out: superadmin, empty, null, or whitespace-only chapter names
      const filtered = response.data.filter((c) => {
        const name = c.chapter_name?.trim().toLowerCase();
        return name && name !== "" && name !== "superadmin";
      });

      setChapters(filtered);
    } catch (err) {
      console.error("Failed to fetch chapters:", err);
      showToast("Failed to load chapters.", "error");
    } finally {
      setLoadingChapters(false);
    }
  };
  fetchChapters();
}, []);

  // ── Fetch PayU configs ──────────────────────────────────────────────────────
  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        setLoadingConfigs(true);
        const response = await axios.get(`${baseurl}/admin/chapter_payu_config`, authHeader());
        // Expects: [{ id, chapter_id, chapter_name, env, payu_key, payu_salt, is_active, created_at }]
        setConfigs(response.data);
      } catch (err) {
        console.error("Failed to fetch configs:", err);
        showToast("Failed to load configurations.", "error");
      } finally {
        setLoadingConfigs(false);
      }
    };
    fetchConfigs();
  }, []);

  // ── Open Add ──
  const handleAdd = () => {
    setEditItem(null);
    setForm(emptyForm);
    setErrors({});
    setShowKey(false);
    setShowSalt(false);
    setView("form");
  };

  // ── Open Edit ──
  const handleEdit = (item) => {
    setEditItem(item);
    setForm({
      chapter_id : item.chapter,
      // env        : item.env,
      payu_key   : item.payu_key,
      payu_salt  : item.payu_salt,
      is_active  : item.is_active,
    });
    setErrors({});
    setShowKey(false);
    setShowSalt(false);
    setView("form");
  };

  // ── Validate ──
  const validate = () => {
    const e = {};
    if (!form.payu_key.trim())  e.payu_key   = "PayU Key is required.";
    // if (!form.env)              e.env        = "Please select an environment.";  // ← add this
    if (!form.payu_salt.trim()) e.payu_salt  = "PayU Salt is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Submit (Add / Edit) ─────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const selectedChapter = chapters.find((c) => c.chapter_id === parseInt(form.chapter_id));
    const payload = { ...form, chapter_id: parseInt(form.chapter_id) };

    try {
      setSubmitting(true);

      if (editItem) {
        // PUT /admin/chapter_payu_config/:id
        const response = await axios.put(
          `${baseurl}/admin/chapter_payu_config/${editItem.id}`,
          payload,
          authHeader()
        );
        setConfigs((prev) =>
          prev.map((c) =>
            c.id === editItem.id
              ? { ...c, ...response.data, chapter_name: selectedChapter?.chapter_name }
              : c
          )
        );
        showToast("Configuration updated successfully!");
      } else {
        // POST /admin/chapter_payu_config
        const response = await axios.post(
          `${baseurl}/admin/chapter_payu_config`,
          payload,
          authHeader()
        );
        setConfigs((prev) => [
          { ...response.data, chapter_name: selectedChapter?.chapter_name },
          ...prev,
        ]);
        showToast("Configuration added successfully!");
      }
      setView("list");
    } catch (err) {
      console.error("Save failed:", err);
      showToast(err.response?.data?.message || "Failed to save configuration.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    try {
      // DELETE /admin/chapter_payu_config/:id
      await axios.delete(
        `${baseurl}/admin/chapter_payu_config/${deleteTarget.id}`,
        authHeader()
      );
      setConfigs((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
      showToast("Configuration deleted.", "error");
    } catch (err) {
      console.error("Delete failed:", err);
      showToast(err.response?.data?.message || "Failed to delete.", "error");
      setDeleteTarget(null);
    }
  };

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      {/* Toast */}
      {toast && (
        <div style={{ ...styles.toast, background: toast.type === "error" ? "#e74c3c" : "#27ae60" }}>
          {toast.message}
        </div>
      )}

      {/* Delete Modal */}
      {deleteTarget && (
        <DeleteModal
          item={deleteTarget}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      <div style={styles.container}>

        {/* ── Header ── */}
        <div style={styles.header}>
          <div>
            <p style={styles.breadcrumb}>Settings / Payment</p>
            <h1 style={styles.title}>PayU Configuration</h1>
          </div>
          {view === "list" && (
            <button onClick={handleAdd} style={styles.addBtn}>
              <PlusIcon /> Add Payment Config
            </button>
          )}
          {view === "form" && (
            <button onClick={() => setView("list")} style={styles.backBtn}>
              <ArrowLeftIcon /> Back to List
            </button>
          )}
        </div>

        {/* ══════════════ LIST VIEW ══════════════ */}
        {view === "list" && (
          <div style={styles.card}>
            {loadingConfigs ? (
              <div style={styles.empty}>
                <div style={styles.loadingSpinner} />
                <p style={styles.emptyText}>Loading configurations...</p>
              </div>
            ) : configs.length === 0 ? (
              <div style={styles.empty}>
                <div style={styles.emptyIcon}>💳</div>
                <p style={styles.emptyText}>No payment configurations yet.</p>
              </div>
            ) : (
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      {["#", "Chapter", "PayU Key", "PayU Salt", "Status", "Created", "Actions"].map((h) => (
                        <th key={h} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {configs.map((item, idx) => (
                      <tr key={item.id} style={styles.tr}>
                        <td style={{ ...styles.td, color: "#999", fontSize: "13px" }}>{idx + 1}</td>

                        {/* chapter_name — from JOIN on backend */}
                        <td style={styles.td}>
                          <span style={styles.chapterBadge}>{item.chapter_name ?? 'All Chapters'}</span>
                        </td>

                        {/* payu_key */}
                        <td style={styles.td}>
                          <code style={styles.keyCode}>{item.payu_key}</code>
                        </td>

                        {/* payu_salt */}
                        <td style={styles.td}>
                          <code style={styles.keyCode}>{item.payu_salt}</code>
                        </td>

                        {/* is_active */}
                        <td style={styles.td}>
                          <span style={{ ...styles.statusDot, background: item.is_active ? "#27ae60" : "#e74c3c" }}>
                            {item.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>

                        {/* created_at */}
                        <td style={{ ...styles.td, color: "#888", fontSize: "13px" }}>
                          {formatDate(item.created_at)}
                        </td>

                        {/* Actions */}
                        <td style={styles.td}>
                          <div style={styles.actions}>
                            <button onClick={() => handleEdit(item)} style={styles.editBtn}>
                              <EditIcon /> Edit
                            </button>
                            <button onClick={() => setDeleteTarget(item)} style={styles.deleteBtn}>
                              <TrashIcon /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ══════════════ FORM VIEW ══════════════ */}
        {view === "form" && (
          <div style={styles.formCard}>
            <div style={styles.formHeader}>
              <h2 style={styles.formTitle}>
                {editItem ? "Edit Payment Configuration" : "Add Payment Configuration"}
              </h2>
              <p style={styles.formSubtitle}>
                {editItem
                  ? `Editing config for ${editItem.chapter_name}`
                  : "Fill in the details to add a new PayU config."}
              </p>
            </div>

            <form onSubmit={handleSubmit} style={styles.form}>

              {/* Chapter */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Chapter <span style={styles.required}>*</span>
                </label>
                <select
                  value={form.chapter_id}
                  onChange={(e) => setForm({ ...form, chapter_id: e.target.value })}
                  disabled={loadingChapters}
                  style={{
                    ...styles.input,
                    ...(errors.chapter_id ? styles.inputError : {}),
                    ...(loadingChapters ? { opacity: 0.6 } : {}),
                  }}
                >
                  <option value="">
                    {loadingChapters ? "Loading chapters..." : "— Select Chapter —"}
                  </option>
                   {/* All Chapters option */}
                  <option value="0">All Chapter</option>
                  {chapters.map((c) => (
                    <option key={c.chapter_id} value={c.chapter_id}>
                      {c.chapter_name}
                    </option>
                  ))}
                </select>
                {errors.chapter_id && <span style={styles.errorMsg}>{errors.chapter_id}</span>}
              </div>

              {/* Env */}
              {/* <div style={styles.formGroup}>
                <label style={styles.label}>Environment</label>
                <select
                  value={form.env}
                  onChange={(e) => setForm({ ...form, env: e.target.value })}
                  style={styles.input}
                >
                  <option value="">— Select Environment —</option>
                  <option value="test">🧪 Test</option>
                  <option value="pretest">📋 PreTest</option>
                  <option value="card">💳 Card</option>
                  <option value="plaque">🏆 Plaque</option>
                  <option value="membership">👤 Membership</option>
                  <option value="priorevidence">📁 PriorEvidence</option>
                </select>
                {errors.env && <span style={styles.errorMsg}>{errors.env}</span>}
              </div> */}
              {/* PayU Key */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  PayU Key <span style={styles.required}>*</span>
                </label>
                <div style={styles.inputWrapper}>
                  <input
                    type={showKey ? "text" : "password"}
                    value={form.payu_key}
                    onChange={(e) => setForm({ ...form, payu_key: e.target.value })}
                    placeholder="Enter PayU merchant key"
                    style={{ ...styles.input, ...styles.inputWithIcon, ...(errors.payu_key ? styles.inputError : {}) }}
                  />
                  <button type="button" onClick={() => setShowKey(!showKey)} style={styles.eyeBtn}>
                    {showKey ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                {errors.payu_key && <span style={styles.errorMsg}>{errors.payu_key}</span>}
              </div>

              {/* PayU Salt */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  PayU Salt <span style={styles.required}>*</span>
                </label>
                <div style={styles.inputWrapper}>
                  <input
                    type={showSalt ? "text" : "password"}
                    value={form.payu_salt}
                    onChange={(e) => setForm({ ...form, payu_salt: e.target.value })}
                    placeholder="Enter PayU salt key"
                    style={{ ...styles.input, ...styles.inputWithIcon, ...(errors.payu_salt ? styles.inputError : {}) }}
                  />
                  <button type="button" onClick={() => setShowSalt(!showSalt)} style={styles.eyeBtn}>
                    {showSalt ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                {errors.payu_salt && <span style={styles.errorMsg}>{errors.payu_salt}</span>}
              </div>

              {/* Is Active */}
              <div style={styles.formGroup}>
                <label style={styles.toggleLabel}>
                  <div
                    onClick={() => setForm({ ...form, is_active: !form.is_active })}
                    style={{ ...styles.toggle, background: form.is_active ? "#2563eb" : "#d1d5db" }}
                  >
                    <div style={{
                      ...styles.toggleKnob,
                      transform: form.is_active ? "translateX(22px)" : "translateX(2px)",
                    }} />
                  </div>
                  <span style={styles.toggleText}>
                    {form.is_active
                      ? "Active — Config will be used for payments"
                      : "Inactive — Config won't be used"}
                  </span>
                </label>
              </div>

              {/* Actions */}
              <div style={styles.formActions}>
                <button type="button" onClick={() => setView("list")} style={styles.cancelFormBtn}>
                  Cancel
                </button>
                <button type="submit" style={styles.submitBtn} disabled={submitting}>
                  {submitting
                    ? "Saving..."
                    : editItem ? "Update Configuration" : "Save Configuration"}
                </button>
              </div>
            </form>
          </div>
        )}

      </div>
    </div>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = {
  page: {
    minHeight: "100vh", background: "#f0f2f7",
    fontFamily: "'Segoe UI', sans-serif", padding: "32px 24px", position: "relative",
  },
  container: { maxWidth: "1200px", margin: "0 auto" },

  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "24px" },
  breadcrumb: { fontSize: "12px", color: "#94a3b8", marginBottom: "4px", letterSpacing: "0.5px" },
  title: { fontSize: "26px", fontWeight: "700", color: "#1e293b", margin: 0 },

  addBtn: {
    display: "flex", alignItems: "center", gap: "8px",
    background: "#2563eb", color: "#fff", border: "none",
    padding: "10px 20px", borderRadius: "10px", fontSize: "14px",
    fontWeight: "600", cursor: "pointer", boxShadow: "0 4px 14px rgba(37,99,235,0.35)",
  },
  backBtn: {
    display: "flex", alignItems: "center", gap: "8px",
    background: "#fff", color: "#475569", border: "1.5px solid #e2e8f0",
    padding: "10px 18px", borderRadius: "10px", fontSize: "14px",
    fontWeight: "600", cursor: "pointer",
  },

  card: { background: "#fff", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,0.07)", overflow: "hidden" },
  tableWrapper: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    padding: "14px 18px", textAlign: "left", fontSize: "11px",
    fontWeight: "700", color: "#94a3b8", textTransform: "uppercase",
    letterSpacing: "0.8px", background: "#f8fafc", borderBottom: "1.5px solid #e8edf3",
  },
  tr: { borderBottom: "1px solid #f1f5f9" },
  td: { padding: "14px 18px", fontSize: "14px", color: "#374151", verticalAlign: "middle" },

  chapterBadge: { fontWeight: "600", color: "#1e293b" },
  keyCode: {
    background: "#f1f5f9", padding: "3px 8px",
    borderRadius: "6px", fontSize: "12px", fontFamily: "monospace", color: "#475569",
  },
  statusDot: {
    display: "inline-flex", alignItems: "center", padding: "3px 10px",
    borderRadius: "20px", fontSize: "12px", fontWeight: "600", color: "#fff",
  },
  actions: { display: "flex", gap: "8px" },
  editBtn: {
    display: "flex", alignItems: "center", gap: "5px",
    background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe",
    padding: "6px 12px", borderRadius: "8px", fontSize: "12px", fontWeight: "600", cursor: "pointer",
  },
  deleteBtn: {
    display: "flex", alignItems: "center", gap: "5px",
    background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca",
    padding: "6px 12px", borderRadius: "8px", fontSize: "12px", fontWeight: "600", cursor: "pointer",
  },

  empty: { padding: "60px 20px", textAlign: "center" },
  emptyIcon: { fontSize: "48px", marginBottom: "12px" },
  emptyText: { color: "#94a3b8", fontSize: "15px", marginBottom: "16px" },
  loadingSpinner: {
    width: "36px", height: "36px", border: "3px solid #e2e8f0",
    borderTop: "3px solid #2563eb", borderRadius: "50%",
    animation: "spin 0.8s linear infinite", margin: "0 auto 12px",
  },

  formCard: { background: "#fff", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,0.07)", overflow: "hidden" },
  formHeader: {
    padding: "24px 32px 20px", borderBottom: "1.5px solid #f1f5f9",
    background: "linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)",
  },
  formTitle: { fontSize: "20px", fontWeight: "700", color: "#1e293b", margin: "0 0 4px" },
  formSubtitle: { fontSize: "14px", color: "#64748b", margin: 0 },
  form: { padding: "28px 32px" },
  formGroup: { marginBottom: "20px" },
  label: { display: "block", fontSize: "13px", fontWeight: "600", color: "#374151", marginBottom: "8px" },
  required: { color: "#ef4444" },
  input: {
    width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0",
    borderRadius: "10px", fontSize: "14px", color: "#1e293b",
    background: "#f8fafc", outline: "none", boxSizing: "border-box",
  },
  inputError: { borderColor: "#ef4444", background: "#fff5f5" },
  inputWrapper: { position: "relative" },
  inputWithIcon: { paddingRight: "42px" },
  eyeBtn: {
    position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)",
    background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: "0",
  },
  errorMsg: { fontSize: "12px", color: "#ef4444", marginTop: "4px", display: "block" },

  radioGroup: { display: "flex", gap: "12px", paddingTop: "4px" },
  radioLabel: { display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" },
  radioText: {
    padding: "8px 16px", borderRadius: "8px", fontSize: "13px",
    fontWeight: "600", border: "1.5px solid #e2e8f0", color: "#64748b",
  },
  radioActive: { borderColor: "#2563eb", background: "#eff6ff", color: "#2563eb" },

  toggleLabel: { display: "flex", alignItems: "center", gap: "12px", cursor: "pointer" },
  toggle: {
    width: "46px", height: "26px", borderRadius: "13px",
    position: "relative", transition: "background 0.3s", cursor: "pointer", flexShrink: 0,
  },
  toggleKnob: {
    position: "absolute", top: "3px", width: "20px", height: "20px",
    background: "#fff", borderRadius: "50%", boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
    transition: "transform 0.3s",
  },
  toggleText: { fontSize: "13px", color: "#475569", fontWeight: "500" },

  formActions: {
    display: "flex", justifyContent: "flex-end", gap: "12px",
    marginTop: "8px", paddingTop: "20px", borderTop: "1px solid #f1f5f9",
  },
  cancelFormBtn: {
    padding: "10px 22px", border: "1.5px solid #e2e8f0", borderRadius: "10px",
    background: "#fff", color: "#475569", fontSize: "14px", fontWeight: "600", cursor: "pointer",
  },
  submitBtn: {
    padding: "10px 28px", border: "none", borderRadius: "10px",
    background: "#2563eb", color: "#fff", fontSize: "14px",
    fontWeight: "600", cursor: "pointer", boxShadow: "0 4px 14px rgba(37,99,235,0.3)",
  },

  modalOverlay: {
    position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, backdropFilter: "blur(4px)",
  },
  modalBox: {
    background: "#fff", borderRadius: "16px", padding: "36px 32px",
    maxWidth: "400px", width: "90%", textAlign: "center",
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  },
  modalIcon: { fontSize: "40px", marginBottom: "12px" },
  modalTitle: { fontSize: "20px", fontWeight: "700", color: "#1e293b", margin: "0 0 10px" },
  modalText: { fontSize: "14px", color: "#64748b", lineHeight: "1.6", margin: "0 0 24px" },
  modalActions: { display: "flex", gap: "12px", justifyContent: "center" },
  cancelBtn: {
    flex: 1, padding: "10px", border: "1.5px solid #e2e8f0", borderRadius: "10px",
    background: "#fff", color: "#475569", fontSize: "14px", fontWeight: "600", cursor: "pointer",
  },
  deleteConfirmBtn: {
    flex: 1, padding: "10px", border: "none", borderRadius: "10px",
    background: "#ef4444", color: "#fff", fontSize: "14px", fontWeight: "600", cursor: "pointer",
  },

  toast: {
    position: "fixed", top: "20px", right: "24px", zIndex: 9999,
    padding: "12px 20px", borderRadius: "10px", color: "#fff",
    fontSize: "14px", fontWeight: "600", boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
  },
};

export default PaymentSetting;

// Inject spinner keyframe
const styleTag = document.createElement("style");
styleTag.innerHTML = `@keyframes spin { to { transform: rotate(360deg); } }`;
document.head.appendChild(styleTag);